import { useEffect, useState } from "react";
import { api } from "./api";

/**
 * Reading a photograph or a PDF with the AI (quotation B1 to B3), from a form.
 *
 * The reading only fills the form. Whoever is at the desk looks it over and presses
 * save, and the fields the model was not sure of are named so that is where they look.
 * When the server has no key, nothing here shows at all.
 */

/** Whether the server can read at all. Asked once per page. */
export function useReading(): boolean {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let live = true;
    void api.readingAvailable().then((yes) => {
      if (live) setAvailable(yes);
    });
    return () => {
      live = false;
    };
  }, []);
  return available;
}

/** A file as the server takes it: its type and its bytes in base64. */
export async function fileForReading(file: File): Promise<{ contentType: string; fileName: string; data: string }> {
  const sent = await smallEnough(file);
  const bytes = new Uint8Array(await sent.arrayBuffer());
  let binary = "";
  for (let at = 0; at < bytes.length; at += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
  }
  const contentType = sent.type || (file.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "image/jpeg");
  return { contentType, fileName: file.name, data: btoa(binary) };
}

/**
 * A photograph made small enough to send, and still sharp enough to read a cert number off
 * (D, 14 October 2026: a phone photo of a slab came back "Request body is too large"). A
 * photo over 3,000 pixels on its long side, or over 3 MB, is redrawn at 3,000 pixels as a
 * JPEG; anything smaller, and any PDF, goes as it is. A photo the browser cannot open, such
 * as some HEIC files, also goes as it is.
 */
const READ_SIDE = 3000;
async function smallEnough(file: File): Promise<Blob> {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  try {
    const image = await createImageBitmap(file);
    const longest = Math.max(image.width, image.height);
    if (longest <= READ_SIDE && file.size <= 3 * 1024 * 1024) {
      image.close();
      return file;
    }
    const scale = Math.min(1, READ_SIDE / longest);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(image.width * scale);
    canvas.height = Math.round(image.height * scale);
    canvas.getContext("2d")?.drawImage(image, 0, 0, canvas.width, canvas.height);
    image.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
    return blob ? new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }) : file;
  } catch {
    return file;
  }
}

/** The fields the model was unsure of, as a sentence for the person to act on. */
export function unsureSentence(unsure: readonly string[], names: Readonly<Record<string, string>>): string | null {
  const words = unsure.map((field) => names[field] ?? field);
  if (words.length === 0) return null;
  return `Read from the picture. Check ${words.join(", ").replace(/, ([^,]*)$/, " and $1")} before saving.`;
}

/** A button that reads a file the person picks, and says when it is working. */
export function ReadFileButton({
  id,
  label,
  accept,
  busy,
  onFile,
}: {
  readonly id: string;
  readonly label: string;
  readonly accept: string;
  readonly busy: boolean;
  readonly onFile: (file: File) => void;
}): React.ReactElement {
  return (
    <label htmlFor={id} className={`ti-btn ti-btn-light !mb-0 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}>
      <i className={busy ? "ri-loader-4-line animate-spin" : "ri-camera-lens-line"} /> {busy ? "Reading" : label}
      <input
        id={id}
        type="file"
        accept={accept}
        className="sr-only"
        disabled={busy}
        onChange={(changed) => {
          const file = changed.target.files?.[0];
          changed.target.value = "";
          if (file) onFile(file);
        }}
      />
    </label>
  );
}
